import { ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/site";

/* ---------------------------------------------------------------
   Button — enterprise pill buttons with restrained motion
   variants: gold | ghost-dark | ghost-light | text
--------------------------------------------------------------- */
export function Btn({
  children,
  variant = "gold",
  to, // section id to scroll to
  onClick,
  className = "",
  withArrow = false,
  type = "button",
  testId,
  ...rest
}) {
  const base =
    "group inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold tracking-tight px-6 py-3 transition-all duration-300 will-change-transform active:translate-y-px";
  const variants = {
    gold:
      "bg-natsu-gold text-natsu-navy hover:bg-natsu-gold-soft shadow-[0_1px_0_rgba(255,255,255,0.25)_inset] hover:-translate-y-0.5",
    "ghost-dark":
      "border border-white/25 text-natsu-warm-white hover:bg-white/5 hover:border-white/45",
    "ghost-light":
      "border border-natsu-navy/15 text-natsu-navy hover:bg-natsu-navy/[0.04] hover:border-natsu-navy/30",
    text:
      "px-0 py-0 text-natsu-blue hover:text-natsu-blue-soft underline-offset-4 hover:underline",
  };

  const handleClick = (e) => {
    if (to) scrollToId(to);
    if (onClick) onClick(e);
  };

  return (
    <button
      type={type}
      data-testid={testId}
      onClick={handleClick}
      className={`${base} ${variants[variant]} ${className}`}
      {...rest}
    >
      {children}
      {withArrow && (
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      )}
    </button>
  );
}

/* ---------------------------------------------------------------
   Eyebrow / section label with a small semantic tick
--------------------------------------------------------------- */
export function Eyebrow({ children, tone = "light", color = "gold", className = "" }) {
  const tick =
    color === "blue"
      ? "bg-natsu-blue"
      : color === "purple"
      ? "bg-natsu-purple"
      : "bg-natsu-gold";
  const text = tone === "dark" ? "text-natsu-warm-white/70" : "text-natsu-slate";
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className={`h-[6px] w-[6px] rounded-full ${tick}`} />
      <span className={`eyebrow ${text}`}>{children}</span>
    </div>
  );
}

/* ---------------------------------------------------------------
   SectionShell — background tone + spacing + container
   tone: white | mist | dark
--------------------------------------------------------------- */
export function SectionShell({ id, tone = "white", children, className = "", grain = false, prominent = false }) {
  const tones = {
    white: "bg-white text-natsu-navy",
    mist: "bg-natsu-mist text-natsu-navy",
    dark: "bg-natsu-navy text-natsu-warm-white on-dark",
  };
  return (
    <section
      id={id}
      className={`relative ${tones[tone]} ${grain ? "grain" : ""} ${className}`}
    >
      <div className={`site-container section-y ${prominent ? "section-prominent" : ""} relative z-[1]`}>{children}</div>
    </section>
  );
}

/* ---------------------------------------------------------------
   PlaceholderBlock — Phase 1 wireframe region
--------------------------------------------------------------- */
export function PlaceholderBlock({
  tag = "Placeholder",
  note,
  minHeight = 320,
  dark = false,
  className = "",
  children,
}) {
  return (
    <div
      className={`ph-block ${dark ? "ph-block--dark" : ""} flex flex-col items-center justify-center text-center px-6 ${className}`}
      style={{ minHeight }}
      data-testid={`placeholder-${tag.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {children ?? (
        <>
          <span
            className={`ph-tag inline-flex items-center gap-2 rounded-full px-3 py-1 ${
              dark
                ? "bg-white/[0.06] text-natsu-warm-white/70"
                : "bg-natsu-navy/[0.04] text-natsu-slate"
            }`}
          >
            <span className="h-[6px] w-[6px] rounded-full bg-natsu-blue" />
            {tag}
          </span>
          {note && (
            <p
              className={`mt-4 measure-sm text-sm leading-relaxed ${
                dark ? "text-natsu-warm-white/55" : "text-natsu-slate"
              }`}
            >
              {note}
            </p>
          )}
          <p
            className={`mt-3 text-xs ${
              dark ? "text-natsu-warm-white/35" : "text-natsu-slate/70"
            }`}
          >
            To be designed &amp; built in Phase 2
          </p>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------
   IconChip — restrained square icon container with semantic tint
--------------------------------------------------------------- */
export function IconChip({ icon: Icon, tint = "blue", tone = "light", className = "" }) {
  const light = {
    blue: "bg-natsu-blue/10 text-natsu-blue ring-natsu-blue/15",
    gold: "bg-natsu-gold/12 text-natsu-amber ring-natsu-gold/20",
    purple: "bg-natsu-purple/10 text-natsu-purple ring-natsu-purple/15",
    green: "bg-natsu-green/10 text-natsu-green ring-natsu-green/15",
    slate: "bg-natsu-navy/[0.05] text-natsu-slate ring-natsu-navy/10",
  };
  const dark = {
    blue: "bg-natsu-blue/15 text-natsu-blue-soft ring-natsu-blue/25",
    gold: "bg-natsu-gold/15 text-natsu-gold ring-natsu-gold/30",
    purple: "bg-natsu-purple/15 text-natsu-purple ring-natsu-purple/25",
    green: "bg-natsu-green/15 text-natsu-green ring-natsu-green/25",
    slate: "bg-white/[0.06] text-natsu-warm-white/70 ring-white/10",
  };
  const map = tone === "dark" ? dark : light;
  return (
    <span
      className={`flex h-11 w-11 items-center justify-center rounded-xl ring-1 ${map[tint]} ${className}`}
    >
      <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
    </span>
  );
}

/* ---------------------------------------------------------------
   FeatureCard — enterprise content card (light sections)
--------------------------------------------------------------- */
export function FeatureCard({ icon, tint = "blue", index, title, children, examples, className = "" }) {
  return (
    <div
      className={`group flex h-full flex-col rounded-2xl border border-natsu-navy/12 bg-white p-5 transition-all duration-300 hover:border-natsu-navy/25 hover:shadow-[0_18px_44px_-24px_rgba(10,23,41,0.28)] sm:p-6 ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3.5">
          {icon && <IconChip icon={icon} tint={tint} className="flex-shrink-0" />}
          <h3 className="pt-1 font-display text-lg leading-snug text-natsu-navy">{title}</h3>
        </div>
        {index != null && (
          <span className="pt-1 font-display text-sm text-natsu-slate/50">
            {String(index).padStart(2, "0")}
          </span>
        )}
      </div>
      {children && (
        <p className="mt-4 text-[14px] leading-relaxed text-natsu-slate">{children}</p>
      )}
      {examples && (
        <ul className="mt-4 space-y-2 border-t border-natsu-navy/10 pt-4">
          {examples.map((e) => (
            <li key={e} className="flex items-center gap-2.5 text-[13px] text-natsu-navy/75">
              <span className="h-1 w-1 flex-shrink-0 rounded-full bg-natsu-gold" />
              {e}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SectionHead({
  label,
  labelColor = "gold",
  tone = "light",
  title,
  intro,
  align = "left",
  size = "lg",
  className = "",
}) {
  const titleSize =
    size === "xl"
      ? "text-4xl sm:text-5xl lg:text-[3.4rem]"
      : "text-3xl sm:text-4xl lg:text-5xl";
  const introColor =
    tone === "dark" ? "text-natsu-warm-white/70" : "text-natsu-slate";
  const titleColor = tone === "dark" ? "text-natsu-warm-white" : "text-natsu-navy";
  return (
    <div className={`${align === "center" ? "mx-auto text-center" : ""} ${className}`}>
      <Eyebrow tone={tone} color={labelColor}>
        {label}
      </Eyebrow>
      <h2
        className={`display ${titleSize} ${titleColor} mt-6 max-w-[20ch] text-balance ${
          align === "center" ? "mx-auto" : ""
        }`}
      >
        {title}
      </h2>
      {intro && (
        <p
          className={`mt-6 measure text-base sm:text-lg leading-relaxed ${introColor} ${
            align === "center" ? "mx-auto" : ""
          }`}
        >
          {intro}
        </p>
      )}
    </div>
  );
}
